import AvatarStack from 'components/AvatarStack';
import type { TableDataValue } from 'components/DataTableTooltip';
import DataTableTooltip from 'components/DataTableTooltip';
import DateTimeFormat from 'components/DateTimeFormat';
import DurationFormat from 'components/DurationFormat';
import { MarkdownAsPlainText } from 'components/RenderedMarkdown';
import RichTooltip from 'components/RichTooltip';
import Truncate from 'components/Truncate';
import React from 'react';
import { useSelector } from 'react-redux';
import { getEnrichedForecasts } from 'routes/forecast/redux/modules/forecast.selectors';
import type { EnrichedForecast, ForecastAssignee } from 'routes/forecast/types/forecast';

export interface ForecastTooltipProps {
  id: number;
  /** Info in tooltip adjusts based on context. e.g Project views will also show Assignee, since not everyone has avatars **/
  viewContext?: 'user' | 'project';
  children?: React.ReactElement;
}

export const ForecastTooltip: React.FC<ForecastTooltipProps> = React.memo(
  ({ id, viewContext = 'user', children }) => {
    const forecasts = useSelector(getEnrichedForecasts);
    const forecast = forecasts.find(f => f.id === id);
    if (!forecast) {
      return (
        <RichTooltip isOpen tooltip={`Task ${id} not found`}>
          {children}
        </RichTooltip>
      );
    }
    const dataTooltip = createForecastTooltipData(forecast, { viewContext });
    return (
      <DataTableTooltip isOpen position="bottom" fixed dataValues={dataTooltip}>
        {children}
      </DataTableTooltip>
    );
  }
);

export function createForecastTooltipData(
  forecast: EnrichedForecast,
  options?: Pick<ForecastTooltipProps, 'viewContext'>
): Array<TableDataValue> {
  let dataTooltip: Array<TableDataValue> = [
    {
      label: forecast.title,
      mask: true,
      header: true,
    },
  ];
  if ((forecast.description?.trim().length ?? 0) > 0) {
    dataTooltip.push(
      <div className="fs-mask">
        <Truncate lines={3}>
          <MarkdownAsPlainText markdown={forecast.description ?? ''} truncate={300} />
        </Truncate>
      </div>
    );
  }

  if (forecast.meta?.isDone) {
    dataTooltip.push({
      label: 'Completed on',
      value: <DateTimeFormat value={forecast.completed_at!} />,
    });
  } else {
    dataTooltip = [
      ...dataTooltip,
      {
        label: 'Due',
        value: (
          <span>
            <DateTimeFormat value={forecast.to} />
          </span>
        ),
        align: 'right',
      },
      {
        label: 'Estimate',
        value: (
          <DurationFormat value={forecast?.estimated_minutes} format="h[h] m[m]" trim="both" />
        ),
        align: 'right',
      },
      {
        label: 'Logged time',
        value: (
          <DurationFormat
            value={forecast?.logged_duration.total_minutes}
            format="h[h] m[m]"
            trim="both"
          />
        ),
        align: 'right',
        secondaryValue: <>{forecast?.meta.progressedPercent}%</>,
      },
      {
        label: 'Planned time',
        value: (
          <DurationFormat
            value={forecast?.planned_duration.total_minutes}
            format="h[h] m[m]"
            trim="both"
          />
        ),
        align: 'right',
        secondaryValue: <>{forecast?.meta.plannedPercent}%</>,
      },
    ];
  }

  // Not useful info when viewing a single user
  if (options?.viewContext !== 'user' || forecast.users.length > 1) {
    dataTooltip.push({
      label: forecast.users.length < 2 ? 'Assignee' : 'Assignees',
      value: displayUsers(forecast.users),
      mask: true,
    });
  }

  return dataTooltip;
}
function displayUsers(users: Array<ForecastAssignee>): React.ReactNode {
  if (users.length === 0) {
    return 'Unassigned';
  }
  if (users.length > 2) {
    return <AvatarStack users={users} size="small" />;
  }
  return users.map(u => u.name).join(', ');
}
